import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Trash2, Lock, Sparkles, Upload, Cog, Download, Zap, DollarSign, Eye } from 'lucide-react';
import ToolGrid from '../components/ToolGrid';
import AdSlot from '../components/AdSlot';
import { api } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { useSEO } from '../lib/seo';

function TrustBadge({ Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-xs md:text-sm text-gray-300">
      <Icon className="w-4 h-4 text-vault-gold" />
      <span>{label}</span>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="text-3xl md:text-4xl font-heading font-bold gold-text tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mt-1">{label}</div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useI18n();
  const [todayCount, setTodayCount] = React.useState(0);

  useSEO({
    title: 'ValutDocu — Free Online PDF Tools | 37 Tools, No Watermarks, AI-Powered',
    description: 'The free, unlimited iLovePDF alternative. 37 PDF tools including AI Chat with PDF, Summarize, Translate, and Extract. No watermarks, no signup, files deleted in 60 min.',
    keywords: 'pdf tools, ilovepdf alternative, merge pdf, compress pdf, pdf to word, ai pdf, free pdf tools online',
    canonical: process.env.REACT_APP_BACKEND_URL,
  });

  React.useEffect(() => {
    let mounted = true;
    api.get('/tools/stats').then(({ data }) => {
      if (!mounted) return;
      setTodayCount(data.today || 0);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden vault-grid-bg" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 -right-20 w-96 h-96 bg-vault-gold/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-20 w-96 h-96 bg-vault-gold/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-10 md:pt-14 md:pb-14">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-vault-gold/30 bg-vault-gold/5 mb-5 animate-fade-up">
              <ShieldCheck className="w-3.5 h-3.5 text-vault-gold" />
              <span className="text-[11px] tracking-[0.18em] uppercase font-mono text-vault-gold">
                {t('hero.eyebrow')}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tighter leading-[1.05] text-white animate-fade-up">
              {t('hero.title.line1')} <span className="gold-text">{t('hero.title.line2')}</span> {t('hero.title.line3')}
            </h1>

            <p className="mt-4 text-base md:text-lg text-gray-300 max-w-xl mx-auto leading-relaxed animate-fade-up">
              37 tools · No watermarks · No signup · Auto-delete in 60 min
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-3 animate-fade-up">
              <Link
                data-testid="hero-start-free"
                to="/merge-pdf"
                className="btn-primary inline-flex items-center gap-2 text-sm md:text-base"
              >
                {t('hero.cta.start')}
              </Link>
              <a
                data-testid="hero-view-tools"
                href="#tools"
                className="btn-ghost inline-flex items-center gap-2 text-sm md:text-base"
              >
                {t('hero.cta.view')}
              </a>
            </div>

            <div className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-xs text-gray-400">
              <span className="inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5 text-vault-gold" /> {t('hero.badge.delete')}</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-vault-gold" /> {t('hero.badge.ssl')}</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-vault-gold" /> {t('hero.badge.watermarks')}</span>
              <span className="inline-flex items-center gap-1.5 font-mono">
                <span data-testid="live-counter" className="gold-text font-bold text-sm">{todayCount.toLocaleString()}</span>
                <span className="text-gray-500">processed today</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* TOOL GRID */}
      <ToolGrid />

      {/* Ad Slot - between hero/tools and how-it-works */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AdSlot slot="home-mid" testId="ad-home-mid" className="my-8" />
      </div>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="max-w-7xl mx-auto px-6 lg:px-8 py-14 border-t border-white/5">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-vault-gold mb-3">Process</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tighter">
            Three steps. <span className="gold-text">Total privacy.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { Icon: Upload, title: 'Upload', desc: 'Drop your file in the vault over a 256-bit encrypted channel.', step: '01' },
            { Icon: Cog, title: 'Process', desc: 'Our optimized Python engine works in isolation — no third parties.', step: '02' },
            { Icon: Download, title: 'Download', desc: 'Grab your file. We delete it from our servers after 60 minutes.', step: '03' },
          ].map(({ Icon, title, desc, step }) => (
            <div key={step} className="tool-card rounded-2xl p-8 relative">
              <span className="absolute top-6 right-6 font-mono text-vault-gold/20 text-5xl font-bold tracking-tighter">
                {step}
              </span>
              <div className="w-14 h-14 rounded-xl bg-vault-gold/10 border border-vault-gold/30 flex items-center justify-center mb-5">
                <Icon className="w-7 h-7 text-vault-gold" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-white mb-2">{title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY VALUTDOCU */}
      <section data-testid="why-section" className="max-w-7xl mx-auto px-6 lg:px-8 py-14 border-t border-white/5">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-vault-gold mb-3">Why ValutDocu</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tighter">
            Built to beat <span className="gold-text">iLovePDF.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { Icon: Zap, title: 'Faster Engine', desc: 'Python + PyMuPDF native code — 3× faster than competitors.' },
            { Icon: ShieldCheck, title: 'Zero Restrictions', desc: 'No file count caps. No watermarks. No forced signups.' },
            { Icon: Sparkles, title: 'AI Built-In', desc: 'Summarize, chat, translate — features iLovePDF doesn\'t offer.' },
            { Icon: DollarSign, title: 'Cheaper Pro', desc: '$2.99/mo vs iLovePDF $7+/mo. Better tools. Half the price.' },
            { Icon: Eye, title: 'True Privacy', desc: 'Files auto-deleted in 60 min. We never read your content.' },
            { Icon: Lock, title: '256-bit Vault', desc: 'AES-256 encryption on transit. Isolated server storage.' },
            { Icon: Trash2, title: 'No Telemetry', desc: 'No tracking pixels. No file analytics. Clean as a vault.' },
            { Icon: Cog, title: 'Hindi + Multi', desc: 'Multilingual UI from day one — built for the Indian market.' },
          ].map((item, i) => (
            <div key={i} className="tool-card rounded-xl p-5">
              <item.Icon className="w-7 h-7 text-vault-gold mb-3" />
              <h4 className="font-heading font-semibold text-white mb-1">{item.title}</h4>
              <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="glass-panel rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-vault-gold/10 rounded-full blur-3xl" />
          </div>
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tighter">
              Your Documents. <span className="gold-text">Secured.</span>
            </h2>
            <p className="mt-4 text-gray-300 max-w-xl mx-auto">
              No signups required. Drop a file in our vault and walk away — your documents
              processed and deleted in under an hour.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link data-testid="cta-merge" to="/merge-pdf" className="btn-primary">Merge PDFs Now</Link>
              <Link data-testid="cta-compress" to="/compress-pdf" className="btn-ghost">Compress a PDF</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
